import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Shield,
  ShieldCheck,
  Eye,
  Brain,
  Cloud,
  Factory,
  Search,
  Satellite,
  FileCheck,
  Server,
  Activity,
  Lock,
  KeyRound,
  Mail,
  Database,
  Network,
  Bug,
  Swords,
  Workflow,
  Cpu,
  Container,
  GitBranch,
  ClipboardCheck,
  Radar,
  HardDrive,
  Fingerprint,
  AlertTriangle,
  Users,
  Building2,
  Zap,
  Gauge,
  ArrowRight,
  CheckCircle2,
  Briefcase,
  Layers,
  Microscope,
  Compass,
  Radio,
  Antenna,
  Globe2,
} from 'lucide-react';

const Section = ({ children, className = '' }) => (
  <section className={`py-16 md:py-24 ${className}`}>
    <div className="container mx-auto px-4 md:px-6">{children}</div>
  </section>
);

const SectionHead = ({ kicker, title, description, light = false }) => (
  <div className="max-w-3xl mb-12">
    {kicker && (
      <div
        className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase mb-4 ${
          light ? 'text-cyan-300' : 'text-cyan-600'
        }`}
      >
        <span className="h-px w-8 bg-current"></span>
        {kicker}
      </div>
    )}
    <h2
      className={`text-3xl md:text-4xl lg:text-5xl font-bold mb-5 leading-tight ${
        light ? 'text-white' : 'text-slate-900'
      }`}
    >
      {title}
    </h2>
    {description && (
      <p className={`text-base md:text-lg leading-relaxed ${light ? 'text-slate-300' : 'text-slate-600'}`}>
        {description}
      </p>
    )}
  </div>
);

const ServiceItem = ({ icon: Icon, title, description }) => (
  <div
    data-testid={`cyber-service-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)}`}
    className="group relative bg-white border border-slate-200 rounded-2xl p-6 hover:border-cyan-500 hover:shadow-xl transition-all duration-300"
  >
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-xl bg-slate-900 group-hover:bg-cyan-500 flex items-center justify-center flex-shrink-0 transition-colors">
        <Icon className="h-6 w-6 text-cyan-400 group-hover:text-white transition-colors" />
      </div>
      <div className="min-w-0">
        <h3 className="text-base md:text-lg font-semibold text-slate-900 mb-2">{title}</h3>
        {description && <p className="text-sm text-slate-600 leading-relaxed">{description}</p>}
      </div>
    </div>
  </div>
);

const OutcomePill = ({ label, light = false }) => (
  <div
    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
      light
        ? 'bg-white/5 border border-white/10 text-slate-200'
        : 'bg-slate-50 border border-slate-200 text-slate-700'
    }`}
  >
    <CheckCircle2 className={`h-4 w-4 ${light ? 'text-cyan-300' : 'text-cyan-600'}`} />
    {label}
  </div>
);

const Cybersecurity = () => {
  const heroHighlights = [
    { icon: Brain, label: 'AI-Powered Security Operations' },
    { icon: Cloud, label: 'Cloud & Multi-Cloud Security' },
    { icon: Factory, label: 'OT / ICS Cyber Security' },
    { icon: Search, label: 'Digital Forensics & IR' },
    { icon: FileCheck, label: 'DPDPA Compliance' },
    { icon: Satellite, label: 'Space Cyber Security' },
    { icon: Radar, label: 'Managed Detection & Response' },
    { icon: Briefcase, label: 'vCISO & Security Advisory' },
  ];

  const itServices = [
    {
      icon: Bug,
      title: 'Vulnerability Assessment & Penetration Testing (VAPT)',
      description:
        'Network, web, mobile, wireless, API and infrastructure testing using industry-leading, threat-driven methodologies.',
    },
    {
      icon: Swords,
      title: 'Red Team Assessment',
      description:
        'Simulated adversarial attacks to evaluate detection, response readiness and control effectiveness.',
    },
    {
      icon: Layers,
      title: 'Security Architecture Review',
      description:
        'Assessment of enterprise security architecture, network design, cloud architecture and resilience posture.',
    },
    {
      icon: Eye,
      title: 'Security Operations Center (SOC) & AI-SOC',
      description:
        '24x7 monitoring powered by AI, automation, threat intelligence and advanced analytics for faster response.',
    },
    {
      icon: Activity,
      title: 'SIEM, SOAR & UEBA',
      description:
        'Centralized visibility, behavioral analytics, automated response orchestration and risk-based monitoring.',
    },
    {
      icon: HardDrive,
      title: 'Endpoint Detection & Response (EDR/XDR)',
      description:
        'Continuous monitoring, ransomware prevention, endpoint visibility and rapid response capabilities.',
    },
    {
      icon: Radar,
      title: 'Threat Intelligence Services',
      description:
        'Strategic, tactical and operational intelligence for proactive threat identification and risk mitigation.',
    },
    {
      icon: Fingerprint,
      title: 'Identity & Access Management (IAM)',
      description:
        'Identity governance, access lifecycle, SSO, MFA, privileged controls and user access reviews.',
    },
    {
      icon: KeyRound,
      title: 'Privileged Access Management (PAM)',
      description:
        'Protection of privileged accounts, administrative governance, session monitoring and credential vaulting.',
    },
    {
      icon: Mail,
      title: 'Email Security',
      description:
        'Protection against phishing, BEC, malware, spoofing and advanced email-borne threats.',
    },
    {
      icon: Database,
      title: 'Data Loss Prevention (DLP)',
      description:
        'Prevent unauthorized access, transfer or leakage of sensitive corporate and customer information.',
    },
    {
      icon: Network,
      title: 'Zero Trust Architecture',
      description:
        'Design and implementation of modern identity-centric security models based on continuous verification.',
    },
    {
      icon: AlertTriangle,
      title: 'Incident Response Services',
      description:
        'Rapid containment, investigation, eradication, recovery and post-incident improvement programs.',
    },
  ];

  const itOutcomes = [
    'Reduced Cyber Risk',
    'Improved Security Visibility',
    'Faster Threat Detection',
    'Enhanced Compliance Readiness',
    'Stronger Security Posture',
  ];

  const cloudServices = [
    { icon: Cloud, title: 'AWS Security Assessment' },
    { icon: Cloud, title: 'Azure Security Assessment' },
    { icon: Cloud, title: 'Google Cloud Security Assessment' },
    {
      icon: Gauge,
      title: 'Cloud Security Posture Management (CSPM)',
      description:
        'Continuous identification and remediation of misconfigurations, excessive permissions and compliance gaps.',
    },
    {
      icon: Bug,
      title: 'Cloud VAPT',
      description:
        'Cloud-focused security testing across workloads, storage, APIs, containers and cloud infrastructure.',
    },
    {
      icon: Container,
      title: 'Container & Kubernetes Security',
      description: 'Protection of containerized workloads, K8s clusters, CI/CD pipelines and orchestration platforms.',
    },
    {
      icon: GitBranch,
      title: 'DevSecOps Security',
      description: 'Integration of security throughout software development and deployment lifecycles.',
    },
    {
      icon: Eye,
      title: 'Cloud SOC Monitoring',
      description: '24x7 monitoring, threat detection, anomaly identification and IR across cloud environments.',
    },
    {
      icon: Layers,
      title: 'Multi-Cloud Security Review',
      description: 'Security architecture review across AWS, Azure, GCP and hybrid infrastructures.',
    },
  ];

  const cloudBenefits = [
    'Secure Cloud Adoption',
    'Continuous Visibility',
    'Reduced Misconfiguration Risks',
    'Faster Compliance Achievement',
    'Improved Operational Security',
  ];

  const otServices = [
    { icon: ClipboardCheck, title: 'OT Security Assessment' },
    { icon: Factory, title: 'ICS / SCADA Security Review' },
    { icon: Bug, title: 'OT VAPT' },
    { icon: Radar, title: 'Industrial Asset Discovery' },
    { icon: Eye, title: 'OT Security Monitoring' },
    { icon: Network, title: 'Industrial Network Segmentation' },
    { icon: FileCheck, title: 'IEC 62443 Readiness & Compliance' },
    { icon: ShieldCheck, title: 'Critical Infrastructure Protection' },
  ];

  const otIndustries = [
    'Power & Utilities',
    'Oil & Gas',
    'Manufacturing',
    'Smart Cities',
    'Transportation',
    'Water Treatment',
    'Telecom Infrastructure',
  ];

  const otOutcomes = [
    'Reduced Operational Risk',
    'Improved Visibility',
    'Regulatory Alignment',
    'Critical Infrastructure Protection',
  ];

  const dfirServices = [
    { icon: AlertTriangle, title: 'Ransomware Investigation' },
    { icon: Microscope, title: 'Malware Analysis' },
    { icon: HardDrive, title: 'Endpoint Forensics' },
    { icon: Cloud, title: 'Cloud Forensics' },
    { icon: Compass, title: 'Root Cause Analysis' },
    { icon: Radar, title: 'Threat Hunting' },
    { icon: Search, title: 'Breach Investigation' },
    { icon: Briefcase, title: 'Incident Response Retainer' },
  ];

  const dfirCapabilities = [
    'Evidence Preservation',
    'Timeline Reconstruction',
    'Threat Actor Analysis',
    'Recovery Guidance',
    'Executive Reporting',
    'Regulatory Support',
  ];

  const spaceServices = [
    { icon: Satellite, title: 'Satellite Security Assessment' },
    { icon: Antenna, title: 'Ground Station Security Review' },
    { icon: Workflow, title: 'Mission Control Security Assessment' },
    { icon: Radio, title: 'TT&C Security Review' },
    { icon: Eye, title: 'Space Security Operations Center' },
    { icon: Radar, title: 'Space Threat Intelligence' },
    { icon: Network, title: 'Zero Trust for Space Operations' },
    { icon: Globe2, title: 'Space Supply Chain Security' },
  ];

  const spaceFocus = [
    'Satellite Protection',
    'Ground Segment Security',
    'Secure Communications',
    'Mission Continuity',
    'Supply Chain Resilience',
  ];

  const dpdpaServices = [
    { icon: ClipboardCheck, title: 'DPDPA Readiness Assessment' },
    { icon: Gauge, title: 'Gap Assessment' },
    { icon: Database, title: 'Data Discovery & Classification' },
    { icon: FileCheck, title: 'Privacy Impact Assessment' },
    { icon: Layers, title: 'Policy & Framework Development' },
    { icon: Users, title: 'Vendor Risk Assessment' },
    { icon: Briefcase, title: 'Audit Support' },
    { icon: ShieldCheck, title: 'Data Protection Officer (DPO) Advisory' },
  ];

  const dpdpaBenefits = [
    'Regulatory Readiness',
    'Reduced Compliance Risk',
    'Stronger Privacy Governance',
    'Improved Data Protection',
  ];

  const managedServices = [
    { icon: Brain, title: 'AI-SOC Services' },
    { icon: Radar, title: 'Managed Detection & Response (MDR)' },
    { icon: Eye, title: 'Threat Intelligence Services' },
    { icon: Factory, title: 'OT Managed Security' },
    { icon: Cloud, title: 'Cloud Security Managed Services' },
    { icon: Search, title: 'Digital Forensics Retainer' },
    { icon: Briefcase, title: 'vCISO Services' },
    { icon: FileCheck, title: 'DPDPA Managed Compliance' },
  ];

  const managedBenefits = [
    '24x7 Monitoring',
    'Reduced Operational Burden',
    'Faster Incident Response',
    'Predictable Security Costs',
    'Access to Specialized Experts',
  ];

  const whySipl = [
    {
      icon: Layers,
      title: 'Multi-Domain Security Expertise',
      description: 'IT + Cloud + OT + Compliance + Digital Forensics under one accountable partner.',
    },
    {
      icon: Building2,
      title: 'Critical Infrastructure Focus',
      description:
        'Specialized capabilities for Telecom, Utilities, Smart Cities, Manufacturing, Government and CI sectors.',
    },
    {
      icon: Brain,
      title: 'AI-Driven Security Operations',
      description: 'Intelligent detection, automated response and advanced threat analytics across the stack.',
    },
    {
      icon: Compass,
      title: 'Vendor-Agnostic Approach',
      description: 'Recommendations focused on business outcomes — not product bias or quota-driven selling.',
    },
    {
      icon: Workflow,
      title: 'End-to-End Security Lifecycle',
      description: 'Assess → Secure → Monitor → Respond → Recover → Comply, delivered as a continuous program.',
    },
  ];

  const lifecycleSteps = ['Assess', 'Secure', 'Monitor', 'Respond', 'Recover', 'Comply'];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Cyber Security & Critical Infrastructure Protection | Sanghvi Infotech</title>
        <meta
          name="description"
          content="Enterprise cybersecurity, AI-SOC, cloud security, OT/ICS security, digital forensics, DPDPA compliance and space cyber security services by Sanghvi Infotech (SIPL)."
        />
        <meta
          name="keywords"
          content="Cybersecurity, AI SOC, MDR, OT Security, ICS Security, Cloud Security, DPDPA, Digital Forensics, Incident Response, Space Cybersecurity, vCISO, Critical Infrastructure Protection"
        />
      </Helmet>

      {/* HERO */}
      <section className="relative bg-slate-950 text-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(6,182,212,0.18),transparent_55%),radial-gradient(ellipse_at_bottom_left,_rgba(59,130,246,0.18),transparent_55%)]"></div>
          <div
            className="absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
          ></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-10 py-20 md:py-28">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-6">
              <Shield className="h-4 w-4 text-cyan-300" />
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-200">
                SIPL Cyber Defense Practice
              </span>
            </div>

            <h1
              data-testid="cyber-hero-title"
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] mb-6"
            >
              Cyber Security, Digital Trust &{' '}
              <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-blue-400 bg-clip-text text-transparent">
                Critical Infrastructure Protection
              </span>
            </h1>

            <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-10 max-w-3xl">
              Protecting Enterprises, Governments, Utilities, Telecom Networks, Smart Cities, Critical Infrastructure
              and Emerging Digital Ecosystems through advanced cyber defense, AI-powered security operations, cloud
              security, OT security, digital forensics, compliance and incident response services.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
              {heroHighlights.map((h, idx) => {
                const Icon = h.icon;
                return (
                  <div
                    key={idx}
                    data-testid={`cyber-highlight-${idx}`}
                    className="flex items-center gap-2 px-3 py-3 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-colors"
                  >
                    <Icon className="h-4 w-4 text-cyan-300 flex-shrink-0" />
                    <span className="text-xs md:text-sm font-medium text-slate-200 leading-tight">
                      {h.label}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button
                  data-testid="cyber-cta-assessment"
                  size="lg"
                  className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 text-base md:text-lg px-7 font-semibold transition-all hover:scale-105"
                >
                  Request Assessment
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  data-testid="cyber-cta-expert"
                  size="lg"
                  variant="outline"
                  className="border-white/30 text-white bg-transparent hover:bg-white hover:text-slate-950 text-base md:text-lg px-7 font-semibold transition-all hover:scale-105"
                >
                  Talk to Security Expert
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 1: IT SECURITY */}
      <Section className="bg-white">
        <SectionHead
          kicker="Section 01 — IT Security"
          title="Enterprise IT Security Services"
          description="Modern organizations face increasingly sophisticated cyber threats targeting networks, applications, users, endpoints, identities and business-critical data. SIPL delivers comprehensive IT security services designed to identify vulnerabilities, strengthen defenses, improve visibility and reduce organizational cyber risk."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {itServices.map((s, i) => (
            <ServiceItem key={i} {...s} />
          ))}
        </div>
        <div className="mt-12 p-6 md:p-8 rounded-2xl bg-slate-50 border border-slate-200">
          <h4 className="text-sm font-semibold tracking-[0.2em] uppercase text-slate-500 mb-4">
            Business Outcomes
          </h4>
          <div className="flex flex-wrap gap-3">
            {itOutcomes.map((o, i) => (
              <OutcomePill key={i} label={o} />
            ))}
          </div>
        </div>
      </Section>

      {/* SECTION 2: CLOUD SECURITY */}
      <Section className="bg-slate-50">
        <SectionHead
          kicker="Section 02 — Cloud Security"
          title="Cloud Security & Multi-Cloud Protection"
          description="As organizations accelerate cloud adoption, security must evolve beyond traditional perimeter controls. SIPL secures cloud-native, hybrid and multi-cloud environments through continuous monitoring, assessment and governance."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {cloudServices.map((s, i) => (
            <ServiceItem key={i} {...s} />
          ))}
        </div>
        <div className="mt-12 p-6 md:p-8 rounded-2xl bg-white border border-slate-200">
          <h4 className="text-sm font-semibold tracking-[0.2em] uppercase text-slate-500 mb-4">Benefits</h4>
          <div className="flex flex-wrap gap-3">
            {cloudBenefits.map((b, i) => (
              <OutcomePill key={i} label={b} />
            ))}
          </div>
        </div>
      </Section>

      {/* SECTION 3: OT / ICS */}
      <Section className="bg-white">
        <SectionHead
          kicker="Section 03 — OT / ICS Security"
          title="Operational Technology & Industrial Control System Security"
          description="Industrial environments require specialized security approaches that balance cybersecurity with operational continuity. SIPL protects critical infrastructure environments while ensuring operational resilience."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {otServices.map((s, i) => (
            <ServiceItem key={i} {...s} />
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-12">
          <div className="p-6 md:p-8 rounded-2xl bg-slate-900 text-white border border-slate-800">
            <h4 className="text-sm font-semibold tracking-[0.2em] uppercase text-cyan-300 mb-4">
              Industries Served
            </h4>
            <div className="flex flex-wrap gap-3">
              {otIndustries.map((ind, i) => (
                <OutcomePill key={i} label={ind} light />
              ))}
            </div>
          </div>
          <div className="p-6 md:p-8 rounded-2xl bg-slate-50 border border-slate-200">
            <h4 className="text-sm font-semibold tracking-[0.2em] uppercase text-slate-500 mb-4">
              Key Outcomes
            </h4>
            <div className="flex flex-wrap gap-3">
              {otOutcomes.map((o, i) => (
                <OutcomePill key={i} label={o} />
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* SECTION 4: DFIR */}
      <Section className="bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
        }}></div>
        <div className="relative">
          <SectionHead
            kicker="Section 04 — DFIR"
            title="Digital Forensics & Incident Response"
            description="When security incidents occur, rapid investigation and evidence-driven response become critical. SIPL provides advanced forensic and incident response capabilities to support containment, recovery and legal defensibility."
            light
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {dfirServices.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={i}
                  data-testid={`cyber-dfir-${i}`}
                  className="group bg-white/5 border border-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/10 hover:border-cyan-400/40 transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center mb-4 group-hover:bg-cyan-500/20 transition-colors">
                    <Icon className="h-6 w-6 text-cyan-300" />
                  </div>
                  <h3 className="text-base font-semibold text-white">{s.title}</h3>
                </div>
              );
            })}
          </div>
          <div className="mt-12 p-6 md:p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm">
            <h4 className="text-sm font-semibold tracking-[0.2em] uppercase text-cyan-300 mb-4">
              Capabilities
            </h4>
            <div className="flex flex-wrap gap-3">
              {dfirCapabilities.map((c, i) => (
                <OutcomePill key={i} label={c} light />
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* SECTION 5: SPACE CYBER */}
      <Section className="bg-white">
        <div className="flex items-center gap-3 mb-3">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-cyan-50 text-cyan-700 border border-cyan-200">
            Emerging Specialized Capability
          </span>
        </div>
        <SectionHead
          kicker="Section 05 — Space Cyber"
          title="Space Cyber Security & Mission Assurance"
          description="As space systems become increasingly interconnected, cyber resilience becomes essential across satellites, ground stations, mission control environments and supply chains. Supporting New Space, Defense, Research, Aerospace and National Security programs."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {spaceServices.map((s, i) => (
            <ServiceItem key={i} {...s} />
          ))}
        </div>
        <div className="mt-12 p-6 md:p-8 rounded-2xl bg-gradient-to-br from-slate-900 to-blue-950 text-white">
          <h4 className="text-sm font-semibold tracking-[0.2em] uppercase text-cyan-300 mb-4">Focus Areas</h4>
          <div className="flex flex-wrap gap-3">
            {spaceFocus.map((f, i) => (
              <OutcomePill key={i} label={f} light />
            ))}
          </div>
        </div>
      </Section>

      {/* SECTION 6: DPDPA */}
      <Section className="bg-slate-50">
        <SectionHead
          kicker="Section 06 — Compliance"
          title="DPDPA Compliance & Privacy Governance"
          description="Enable compliance with India's Digital Personal Data Protection Act (DPDPA) through structured assessments, governance frameworks, privacy controls and operational readiness programs."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {dpdpaServices.map((s, i) => (
            <ServiceItem key={i} {...s} />
          ))}
        </div>
        <div className="mt-12 p-6 md:p-8 rounded-2xl bg-white border border-slate-200">
          <h4 className="text-sm font-semibold tracking-[0.2em] uppercase text-slate-500 mb-4">
            Business Benefits
          </h4>
          <div className="flex flex-wrap gap-3">
            {dpdpaBenefits.map((b, i) => (
              <OutcomePill key={i} label={b} />
            ))}
          </div>
        </div>
      </Section>

      {/* SECTION 7: MANAGED SECURITY */}
      <Section className="bg-white">
        <SectionHead
          kicker="Section 07 — Managed Services"
          title="Managed Security Services"
          description="Extend your security team with SIPL's managed cybersecurity services delivering continuous protection, monitoring, response and advisory support."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {managedServices.map((s, i) => (
            <ServiceItem key={i} {...s} />
          ))}
        </div>
        <div className="mt-12 p-6 md:p-8 rounded-2xl bg-slate-50 border border-slate-200">
          <h4 className="text-sm font-semibold tracking-[0.2em] uppercase text-slate-500 mb-4">
            Service Benefits
          </h4>
          <div className="flex flex-wrap gap-3">
            {managedBenefits.map((b, i) => (
              <OutcomePill key={i} label={b} />
            ))}
          </div>
        </div>
      </Section>

      {/* WHY SIPL */}
      <Section className="bg-gradient-to-br from-slate-900 via-slate-950 to-blue-950 text-white">
        <SectionHead
          kicker="The SIPL Advantage"
          title="Why Organizations Choose SIPL"
          description="A strategic cybersecurity partner with deep expertise across IT, Cloud, OT, Compliance and Digital Forensics — engineered for critical environments where downtime is not an option."
          light
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {whySipl.map((item, i) => {
            const Icon = item.icon;
            return (
              <Card
                key={i}
                data-testid={`cyber-why-sipl-${i}`}
                className="bg-white/5 border border-white/10 backdrop-blur-sm text-white hover:bg-white/10 hover:border-cyan-400/40 transition-all"
              >
                <CardContent className="p-6 md:p-7">
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center mb-5">
                    <Icon className="h-6 w-6 text-cyan-300" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Lifecycle stepper */}
        <div className="mt-14">
          <h4 className="text-sm font-semibold tracking-[0.2em] uppercase text-cyan-300 mb-6 text-center">
            End-to-End Security Lifecycle
          </h4>
          <div className="hidden md:flex items-center justify-between max-w-5xl mx-auto">
            {lifecycleSteps.map((step, i) => (
              <React.Fragment key={i}>
                <div className="flex flex-col items-center text-center">
                  <div className="w-14 h-14 rounded-full bg-cyan-500/10 border-2 border-cyan-400 flex items-center justify-center text-cyan-300 font-bold">
                    {i + 1}
                  </div>
                  <span className="mt-3 text-sm font-semibold text-white">{step}</span>
                </div>
                {i < lifecycleSteps.length - 1 && (
                  <div className="flex-1 h-px bg-gradient-to-r from-cyan-400/60 to-blue-500/60 mx-3"></div>
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="md:hidden flex flex-wrap justify-center gap-3">
            {lifecycleSteps.map((step, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10"
              >
                <span className="text-cyan-300 font-bold text-sm">{i + 1}</span>
                <span className="text-sm text-white font-medium">{step}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* FINAL CTA */}
      <section className="relative py-20 md:py-28 bg-cyan-400 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(15,23,42,0.4), transparent 50%), radial-gradient(circle at 80% 70%, rgba(15,23,42,0.4), transparent 50%)',
        }}></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-4xl">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-950 mb-6 leading-tight">
              Secure Your Digital Future
            </h2>
            <p className="text-base md:text-lg text-slate-800 mb-10 max-w-3xl leading-relaxed">
              Whether securing enterprise networks, cloud platforms, industrial environments, smart city
              infrastructure, critical national assets or emerging space technologies, SIPL delivers the expertise,
              technology and operational excellence required to stay resilient against evolving cyber threats.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button
                  data-testid="cyber-final-cta-assessment"
                  size="lg"
                  className="bg-slate-950 text-white hover:bg-slate-800 text-base md:text-lg px-7 font-semibold transition-all hover:scale-105"
                >
                  Schedule a Security Assessment
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  data-testid="cyber-final-cta-specialist"
                  size="lg"
                  variant="outline"
                  className="border-slate-950 text-slate-950 bg-transparent hover:bg-slate-950 hover:text-white text-base md:text-lg px-7 font-semibold transition-all hover:scale-105"
                >
                  Speak with a Cyber Security Specialist
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cybersecurity;
